/**
 * The jurisdiction, as the screens see it (ADR-013).
 *
 * Every label that differs between India and the US -- GSTIN or EIN, HSN or product code,
 * "Tax Invoice" or "Invoice", CGST + SGST or one Sales Tax column -- comes from the server
 * through this hook. None of it is a constant in the client, because the day a deployment
 * is switched, a screen still saying GSTIN over a box that now validates an EIN is worse
 * than either country on its own.
 *
 * While the request is in flight the fallback below stands in. It is India's, which is
 * what every deployment of this system has been so far, and it is only ever a placeholder
 * for one paint: labels are not money, and a heading that corrects itself a moment later
 * costs nothing next to a blank screen on every route that shows a figure.
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'

import { getRegime, INDIA_GST, type RegimeInfo } from '@/features/company/api'
import { setMoneyDefaults } from '@/lib/money'

/** What the screens assume until the server answers. Deliberately India's. */
export const FALLBACK_REGIME: RegimeInfo = {
  code: INDIA_GST,
  name: 'India — GST',
  country: 'India',
  currency: 'INR',
  locale: 'en-IN',
  document_title: 'Tax Invoice',
  registration_label: 'GSTIN',
  customer_registration_label: 'GSTIN',
  product_code_label: 'HSN',
  place_label: 'Place of supply',
  rounds_to_whole_currency: true,
  component_labels: {
    intra_state: ['CGST', 'SGST', null],
    inter_state: [null, null, 'IGST'],
    none: [null, null, null],
  },
  rate_slabs: ['0', '5', '12', '18', '28'],
  states: [],
  available: [],
}

/**
 * The regime in force, or the one a given document was raised under.
 *
 * Cached for the session: a jurisdiction changes about once in the life of a deployment,
 * and refetching its labels on every screen would be a request per navigation for an
 * answer that has not moved.
 */
export function useRegime(code?: string | null): RegimeInfo {
  const query = useQuery({
    queryKey: ['tax-regime', code ?? 'active'],
    queryFn: () => getRegime(code),
    staleTime: Infinity,
    // Hold the last regime while a different one is being fetched. Falling back to India
    // for those few milliseconds is not only a flicker: the jurisdiction dropdown on the
    // settings form would briefly lose the option the user has just picked, and a select
    // whose value is no longer among its options is emptied by the browser.
    placeholderData: keepPreviousData,
    retry: false,
  })

  const active = code == null
  const currency = query.data?.currency
  const locale = query.data?.locale

  // Only the regime in force sets the default. A screen looking at an old invoice asks for
  // that invoice's regime, and the rest of the app must not start counting in its currency.
  useEffect(() => {
    if (active && currency) setMoneyDefaults(currency, locale)
  }, [active, currency, locale])

  return query.data ?? FALLBACK_REGIME
}

/**
 * How a supply of this type is taxed, in the regime's own words.
 *
 * Built from the component labels rather than written out per country: India reads
 * "Within the state — CGST + SGST", the US reads "Within the state — Sales Tax", and a
 * third jurisdiction added later reads whatever its own slots are called without this
 * function being touched.
 */
export function supplyLabel(regime: RegimeInfo, supplyType: string): string {
  const where =
    supplyType === 'intra_state'
      ? 'Within the state'
      : supplyType === 'inter_state'
        ? 'Across a state line'
        : 'Not classified'
  const parts = (regime.component_labels[supplyType] ?? []).filter(Boolean)
  return parts.length ? `${where} — ${parts.join(' + ')}` : where
}
